import { createEffect, createSignal, onCleanup, onMount, untrack } from "solid-js"
import { DatoxComplianceDashboard, DatoxPublishedDashboard } from "./react-dashboard/DatoxComplianceDashboard.jsx"
import type { DatoxDashboard, DatoxDashboardDataset, DatoxDashboardSourceExplanation, DatoxKpiResult } from "@/datox/api"
import type { ReactNode } from "react"
import type { Root } from "react-dom/client"
import type { DashboardKpiDataset } from "@/datox/dashboard-kpi"
import type { EvidenceReference } from "@opencode-ai/session-ui/evidence-reference"

type DashboardConfig = Record<string, unknown>

type OutputTable = {
  datasetID: string
  name: string
  headers: string[]
  rows: Array<Record<string, string | number | boolean | null>>
  loadError?: boolean
}

export function DatoxDashboardReactHost(props: {
  projectID: string
  sourceKey: string
  kpiSourceKeys: Record<string, string>
  kpiValues: Record<string, DatoxKpiResult>
  onEvaluateKpi: (
    widget: Record<string, unknown>,
    filterWidgets: Array<Record<string, unknown>>,
    evidenceLimit: number,
  ) => Promise<DatoxKpiResult>
  dashboards: DatoxDashboard[]
  datasets: DatoxDashboardDataset[]
  outputTables: OutputTable[]
  kpiDatasets: Record<string, DashboardKpiDataset>
  locale: string
  dashboardShareReviewers: Array<{
    approvalIds: string[]
    name: string
    email: string
    clientValue?: string
    status: string
  }>
  selectedDashboardID: string
  selectedDatasetID: string
  config: DashboardConfig
  configSyncKey: number
  readOnly?: boolean
  saving: boolean
  dirty: boolean
  loading: boolean
  loadingTables?: boolean
  onRetryDataset?: () => Promise<void>
  error: string
  labels: Record<string, unknown>
  onCreateDashboard: (name: string) => void
  onDeleteDashboard: () => void
  onPublishDashboard: () => Promise<string>
  onUnpublishDashboard: () => void
  onShareAccess: (
    reviewers: Array<{ name?: string; email: string; clientValue?: string }>,
    message?: string,
  ) => Promise<string>
  onRemoveShareReviewer: (approvalIds: string[]) => Promise<void>
  onSelectDashboard: (dashboardID: string) => void
  onSelectDataset: (datasetID: string) => void
  onConfigChange: (config: DashboardConfig) => void
  onLoadPdfTables: (ids: string[]) => Promise<OutputTable[]>
  onExportDashboardPdf: (html: string, filename: string) => Promise<Blob>
  onExplainSource?: (request: ExplainSourceRequest, signal?: AbortSignal) => Promise<DatoxDashboardSourceExplanation>
  onOpenEvidence?: (reference: Omit<EvidenceReference, "sessionID" | "projectID">) => void
}) {
  let element: HTMLDivElement | undefined
  let ReactRuntime: { createElement: (component: unknown, props: Record<string, unknown>) => ReactNode } | undefined
  let root: Root | undefined
  const [ready, setReady] = createSignal(false)
  const saveStatusStore = createSaveStatusStore()

  onMount(async () => {
    if (!element) return
    // @ts-ignore React is intentionally provided by the optional dashboard island dependencies.
    ReactRuntime = await import("react")
    // @ts-ignore ReactDOM is intentionally provided by the optional dashboard island dependencies.
    const ReactDomClient = await import("react-dom/client")
    root = ReactDomClient.createRoot(element)
    setReady(true)
  })

  createEffect(() => {
    saveStatusStore.set(props.saving, props.dirty)
  })

  createEffect(() => {
    if (!ready() || !ReactRuntime) return
    props.configSyncKey
    root?.render(
      ReactRuntime.createElement(props.readOnly ? DatoxPublishedDashboard : DatoxComplianceDashboard, {
        dashboards: props.dashboards,
        projectID: props.projectID,
        datasets: props.datasets,
        outputTables: props.outputTables,
        kpiDatasets: props.kpiDatasets,
        kpiValues: props.kpiValues,
        onEvaluateKpi: props.onEvaluateKpi,
        sourceKey: props.sourceKey,
        kpiSourceKeys: props.kpiSourceKeys,
        locale: props.locale,
        dashboardShareReviewers: props.dashboardShareReviewers,
        selectedDashboardID: props.selectedDashboardID,
        selectedDatasetID: props.selectedDatasetID,
        config: untrack(() => props.config),
        configSyncKey: props.configSyncKey,
        saveStatusStore,
        loading: props.loading,
        loadingTables: props.loadingTables,
        onRetryDataset: props.onRetryDataset,
        error: props.error,
        labels: props.labels,
        onCreateDashboard: props.onCreateDashboard,
        onDeleteDashboard: props.onDeleteDashboard,
        onPublishDashboard: props.onPublishDashboard,
        onUnpublishDashboard: props.onUnpublishDashboard,
        onShareAccess: props.onShareAccess,
        onRemoveShareReviewer: props.onRemoveShareReviewer,
        onSelectDashboard: props.onSelectDashboard,
        onSelectDataset: props.onSelectDataset,
        onConfigChange: props.onConfigChange,
        onLoadPdfTables: props.onLoadPdfTables,
        onExportDashboardPdf: props.onExportDashboardPdf,
        onExplainSource: props.onExplainSource,
        onOpenEvidence: props.onOpenEvidence,
      }),
    )
  })

  onCleanup(() => root?.unmount())

  return <div ref={(node) => (element = node)} class="h-full min-h-0" />
}

type ExplainSourceRequest = {
  dashboardID: string
  dashboardName: string
  tabID: string
  tabName: string
  widgetID: string
  widgetType: string
  widgetTitle: string
  widgetData: Record<string, unknown>
}

function createSaveStatusStore() {
  let value = "0:0"
  const listeners = new Set<() => void>()
  return {
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    getSnapshot() {
      return value
    },
    set(saving: boolean, dirty: boolean) {
      const next = `${Number(saving)}:${Number(dirty)}`
      if (next === value) return
      value = next
      listeners.forEach((listener) => listener())
    },
  }
}
